import { appSelectors } from '@appStore/reducers/app'
import MoreHorizIcon from '@mui/icons-material/MoreHoriz'
import { useStore } from '@nanostores/react'
import attach from 'assets/image/icons/svg/attach.svg'
import star from 'assets/image/icons/svg/star.svg'
import user from 'assets/image/icons/svg/user.svg'
import clsx from 'clsx'
import { $conversations } from 'features/messaging/model/stores'
import { memo, useMemo } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from 'shared/hooks'

import type { Message, Participant, RenderContent } from '../../model/types'

import { api } from '../../api'
import {
	findRenderContent,
	formatDateTime,
	getConversationUrl
} from '../../utils/utils'
import { openActions, useAnchorClass } from '../QuickActions'
import css from './Thumbnail.module.css'

const contentTypePreviewText: Partial<Record<keyof RenderContent, string>> = {
	audio: 'a voice message',
	external_media: 'a GIF',
	file: 'an attachment',
	unsupported_content: 'a post',
	vector_image: 'an attachment',
	video: 'a video',
	video_meeting: 'a video meeting'
}

function PreviewText({
	conversationalist,
	conversationPreviewText,
	conversationType,
	isInterlocutor,
	message,
	renderContent
}: {
	conversationalist?: Participant
	conversationPreviewText: null | string
	conversationType: null | string

	isInterlocutor: boolean
	message?: Message
	renderContent: keyof RenderContent | null
}) {
	const sender = isInterlocutor
		? `${conversationalist?.firstName} sent`
		: 'You sent'

	const subject = message?.subject || conversationPreviewText
	const isReply = renderContent === 'replied_message_content'

	if (renderContent && !isReply) {
		const showAttachIcon =
			(!subject && renderContent === 'vector_image') || renderContent === 'file'

		let text = subject || contentTypePreviewText[renderContent]

		if (!conversationType) {
			text = `${sender} ${text}`
		}

		if (showAttachIcon) {
			return (
				<p className={css.message}>
					{conversationType && (
						<span className={css.type}>{conversationType}</span>
					)}
					<img alt='attach' height={14} src={attach} width={14} />
					<span>{text}</span>
				</p>
			)
		}

		return (
			<>
				{conversationType && (
					<span className={css.type}>{conversationType}</span>
				)}
				{` ${text}`}
			</>
		)
	}

	return subject || message?.text || 'No message'
}

function Thumbnail_({
	id,
	isSelected = false
}: { id: string; isSelected?: boolean }) {
	const conversation = useStore($conversations, {
		keys: [id]
	})[id]

	const {
		conversation_type: conversationType,

		events,
		headline,
		is_starred: isStarred,
		participants,
		unread_count: unread
	} = conversation

	const dispatch = useAppDispatch()
	const [readMutation] = api.useMarkReadMutation()

	const userEntityId = useAppSelector(appSelectors.personIdLi)

	const message: Message | undefined = events[0]

	const openClass = useAnchorClass(id)

	const isMessageDeleted = message?.render_format === 'RECALLED'
	const isSystemMessage = message?.render_format === 'SYSTEM'

	const conversationalist = useMemo(
		() =>
			participants.find(participant => participant.entityId !== userEntityId),
		[participants, userEntityId]
	)

	const isInterlocutor = conversationalist?.entityId === message?.from?.entityId
	const renderContent = findRenderContent(message?.render_content)
	const isTextMessage =
		(message &&
			message.render_content.some(t => t.replied_message_content !== null)) ||
		message.render_content.length === 0

  const handleMailboxItemClick = () => {
		dispatch(api.util.upsertQueryData('participants', id, participants))
		if (unread) {
			readMutation({ id, mark: true })
		}
	}
  const location = useLocation()
	const showUnread = Boolean(unread)

	return (
		<li className={css.root}>
			{participants.length > 2 ? (
				<div className={css.avatars}>
					{getGroupAvatarUrls(participants)
						.toSorted()
						.map((src, i) => (
							<img
								alt='avatar'
								className={css.avatarItem}
								key={i}
								src={src}
							/>
						))}
				</div>
			) : (
				<img
					alt='avatar'
					className={css.avatar}
					src={conversationalist?.pictureUrl || user}
				/>
			)}

      <Link
				aria-current={isSelected && 'page'}
				className={css.title}
				onClick={handleMailboxItemClick}
        to={{ pathname: getConversationUrl(id), search: location.search }}
			>
				{participants.length > 2 ? 'Group' : conversationalist?.fullName}
			</Link>

			<button
				aria-haspopup='true'
				aria-label='Open menu'
				className={clsx(css.actionTrigger, openClass)}
				onClick={openActions}
				type='button'
				value={id}
			>
				<MoreHorizIcon />
			</button>
			<time className={css.time} dateTime={message.created_at}>
				{formatDateTime(message.created_at)}
			</time>

			{headline && <p className={css.text}>{headline}</p>}
			<div className={css.body}>
				{isMessageDeleted ? (
					<p className='mailbox-user-text'>This message has been deleted.</p>
				) : (
					<div className='mailbox-user-text'>
						{isTextMessage && !isSystemMessage && (
							<span
								className={clsx(
									conversationType && 'mailbox-conversation-type'
								)}
							>
								{conversationType
									? `${conversationType} `
									: isInterlocutor
										? `${conversationalist?.firstName}: `
										: 'You: '}
							</span>
						)}
						<PreviewText
							conversationalist={conversationalist}
							conversationPreviewText={conversation.preview_text}
							conversationType={conversationType}
							isInterlocutor={isInterlocutor}
							message={message}
							renderContent={renderContent}
						/>
					</div>
				)}
				{isStarred && <img alt='star' className='mailbox-star' src={star} />}
				{showUnread && <div className='mailbox-unread'>{unread}</div>}
			</div>
		</li>
	)
}

export const Thumbnail = memo(Thumbnail_)

function getGroupAvatarUrls(
	participants: Participant[]
): string[] {
	const urls: string[] = []
	const added = new Set<string>()

		for (const participant of participants) {
			if (added.has(participant.entityId)){
				continue
			}

			urls.push(participant.pictureUrl || user)
			if (urls.length >= 4){
				break
			}
		}

	return urls.sort((a, b) => b.localeCompare(a))
}
